import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { studioApi, idempotencyKey } from '../../../../../data/studioApi';
import { EDITOR_STEPS, stepOfField } from '../../../../../enums/automations';
import { STUDIO_AUTOMATIONS_CONNECT, automationPath } from '../../../../../enums/routes';
import deepEqual from '../../../../../utils/equal';
import { halfRows, toRequest } from '../../../utils/request';

const AUTOSAVE_MS = 1200;
const MEDIA_BATCH = 12;

const setIn = (object, path, value) => {
  const [key, ...rest] = path.split('.');
  const node = Array.isArray(object) ? [...object] : { ...object };
  node[key] = rest.length ? setIn(object[key], rest.join('.'), value) : value;
  return node;
};
const pickConfig = ({ name, trigger, comment_reply: reply, dm }) => ({ name, trigger, comment_reply: reply, dm });
const changedSections = (request, sent) => Object.fromEntries(Object.entries(request).filter(([key, value]) => !deepEqual(value, sent[key])));

// The editor (contract 2.14.0: PATCH /automations/{id}): a draft or paused automation saves itself
// a moment after each change, an active one on "Salvar alterações" (edits apply to the next
// comments), and activating sends the rules back as field errors or a state to explain.
export default function useAutomationEditor(s, id) {
  const { t } = useTranslation('studioAutomations');
  const [status, setStatus] = useState('loading');
  const [draft, setDraft] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [step, setStep] = useState(EDITOR_STEPS[0]);
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState('saved');
  const [busy, setBusy] = useState(null);
  const [gate, setGate] = useState(null);
  const [media, setMedia] = useState({ items: [], cursor: null, more: false, state: 'idle' });
  const [known, setKnown] = useState({});
  const version = useRef(null);
  const sent = useRef(null);
  const latest = useRef(null);
  const inFlight = useRef(null);
  const live = useRef(true);
  latest.current = draft;
  const channel = draft?.trigger.channel;
  const profileId = s.profile.id;
  const isActive = automation?.status === 'active';

  useEffect(() => () => { live.current = false; }, []);
  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const read = await studioApi.automation(id);
      if (!live.current) return;
      version.current = read.version;
      const config = pickConfig(read);
      sent.current = toRequest(config);
      setAutomation(read);
      setDraft(config);
      setErrors({});
      setSaveState('saved');
      setStatus('ready');
    } catch (failure) { if (live.current) setStatus(failure.code === 'not_found' ? 'missing' : 'error'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // One PATCH at a time, each with the version the last one answered.
  const persist = useCallback(async (extra = {}) => {
    while (inFlight.current) await inFlight.current.catch(() => null);
    const request = latest.current ? toRequest(latest.current) : null;
    const body = request ? changedSections(request, sent.current) : {};
    if (!Object.keys(body).length && !Object.keys(extra).length) { setSaveState('saved'); return automation; }
    setSaveState('saving');
    const call = studioApi.updateAutomation(id, { expected_version: version.current, ...body, ...extra }, idempotencyKey('automation-save'));
    inFlight.current = call;
    try {
      const next = await call;
      version.current = next.version;
      sent.current = { ...sent.current, ...body };
      if (live.current) {
        setAutomation(next);
        setSaveState(deepEqual(toRequest(latest.current), sent.current) ? 'saved' : 'dirty');
      }
      return next;
    } finally { inFlight.current = null; }
  }, [automation, id]);

  // A draft or paused automation saves itself.
  useEffect(() => {
    if (!draft || status !== 'ready' || isActive || deepEqual(toRequest(draft), sent.current)) return undefined;
    setSaveState('dirty');
    const timer = setTimeout(() => persist().catch(failure => {
      if (!live.current) return;
      if (failure.code === 'version_conflict') setGate('conflict');
      else if (failure.errors && Object.keys(failure.errors).length) { setErrors(failure.errors); setSaveState('failed'); } else setSaveState('failed');
    }), AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [draft, isActive, persist, status]);
  useEffect(() => { if (isActive && draft) setSaveState(deepEqual(toRequest(draft), sent.current) ? 'saved' : 'dirty'); }, [draft, isActive]);
  // Leaving keeps what was typed; a failure here has no screen left to show on.
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const activeRef = useRef(isActive);
  activeRef.current = isActive;
  useEffect(() => () => { if (latest.current && !activeRef.current) persistRef.current().catch(() => null); }, []);

  const update = useCallback((path, value) => {
    setDraft(current => setIn(current, path, value));
    setErrors(current => (Object.keys(current).some(field => field === path || field.startsWith(`${path}.`)) ? Object.fromEntries(Object.entries(current).filter(([field]) => field !== path && !field.startsWith(`${path}.`))) : current));
  }, []);
  // The channel decides what else applies: no public reply outside comments, no media in the Direct.
  const setChannel = useCallback(next => {
    setDraft(current => ({
      ...current,
      trigger: { ...current.trigger, channel: next, media_ids: [], media_scope: next === 'direct_messages' ? 'all' : current.trigger.media_scope === 'all' && current.trigger.channel === 'direct_messages' ? 'selected' : current.trigger.media_scope },
      comment_reply: next === 'comments' ? current.comment_reply : { ...current.comment_reply, enabled: false },
    }));
    setErrors({});
  }, []);

  // ── The media the trigger can aim at ──
  const loadMedia = useCallback(async (cursor = null) => {
    if (!channel || channel === 'direct_messages') { setMedia({ items: [], cursor: null, more: false, state: 'idle' }); return; }
    setMedia(current => ({ ...current, state: 'loading', ...(cursor ? {} : { items: [] }) }));
    try {
      const page = await studioApi.media({ profile_id: profileId, channel, limit: MEDIA_BATCH, ...(cursor ? { cursor } : {}) });
      if (!live.current) return;
      setKnown(current => ({ ...current, ...Object.fromEntries(page.data.map(item => [item.id, item])) }));
      setMedia(current => ({ items: cursor ? [...current.items, ...page.data] : page.data, cursor: page.next_cursor, more: page.has_more, state: 'ready' }));
    } catch (failure) {
      if (live.current) setMedia({ items: [], cursor: null, more: false, state: failure.code === 'connection_required' ? 'no_connection' : 'error' });
    }
  }, [channel, profileId]);
  useEffect(() => { loadMedia(); }, [loadMedia]);
  // The selection may reach past the first batch: its covers still show.
  const mediaIdsKey = draft?.trigger.media_ids.join() ?? '';
  useEffect(() => {
    if (!mediaIdsKey) return;
    mediaIdsKey.split(',').filter(mediaId => !known[mediaId]).forEach(mediaId => {
      studioApi.mediaItem(mediaId).then(item => { if (live.current) setKnown(current => ({ ...current, [item.id]: item })); }).catch(() => null);
    });
  }, [mediaIdsKey, known]);
  const toggleMedia = useCallback((mediaId, limit) => {
    setDraft(current => {
      const ids = current.trigger.media_ids;
      const next = ids.includes(mediaId) ? ids.filter(item => item !== mediaId) : ids.length >= limit ? ids : [...ids, mediaId];
      return setIn(current, 'trigger.media_ids', next);
    });
    setErrors(current => { const { 'trigger.media_ids': gone, ...rest } = current; return rest; });
  }, []);

  // ── Going live ──
  const openFirstError = useCallback(fields => {
    const first = EDITOR_STEPS.find(item => Object.keys(fields).some(field => stepOfField(field) === item));
    if (first) setStep(first);
  }, []);
  const refuse = useCallback(failure => {
    switch (failure.code) {
      case 'plan_upgrade_required':
        setErrors(failure.errors || {});
        s.automations.upgrade(s.automations.planForFields(failure.errors || {}));
        return;
      case 'automation_limit_reached': setGate('limit'); return;
      case 'connection_required': setGate('connection'); return;
      case 'version_conflict': setGate('conflict'); return;
      case 'keyword_conflict':
      case 'validation_error':
        setErrors(failure.errors || {});
        openFirstError(failure.errors || {});
        return;
      default: s.act(() => { throw failure; });
    }
  }, [openFirstError, s]);
  // A link typed halfway is not sent, so it is said here before the API hears of it.
  const localErrors = useCallback(() => {
    const { dm } = latest.current;
    const rows = [...halfRows('dm.delivery', dm.delivery), ...(dm.reminder.enabled ? halfRows('dm.reminder.message', dm.reminder.message) : [])];
    return Object.fromEntries(rows.map(([field, key]) => [field, [t(key)]]));
  }, [t]);
  const guarded = useCallback(async (kind, work) => {
    const local = localErrors();
    if (Object.keys(local).length) { setErrors(local); openFirstError(local); return null; }
    setBusy(kind);
    try { return await work(); } catch (failure) { refuse(failure); return null; } finally { if (live.current) setBusy(null); }
  }, [localErrors, openFirstError, refuse]);

  const activate = useCallback(() => guarded('activate', async () => {
    const next = await persist({ status: 'active' });
    setErrors({});
    s.automations.refresh();
    window.setSnackbar?.(t('activated', { name: next.name }));
    s.navigate(automationPath(id));
    return next;
  }), [guarded, id, persist, s, t]);
  const saveActive = useCallback(() => guarded('save', async () => {
    const next = await persist();
    setErrors({});
    window.setSnackbar?.(t('savedActive'));
    return next;
  }), [guarded, persist, s, t]);

  const steps = useMemo(() => EDITOR_STEPS.map(key => ({ key, errors: Object.keys(errors).filter(field => stepOfField(field) === key).length })), [errors]);
  const goNext = useCallback(() => {
    const next = EDITOR_STEPS[EDITOR_STEPS.indexOf(step) + 1];
    if (next) setStep(next);
    if (!isActive) persist().catch(() => setSaveState('failed'));
  }, [isActive, persist, step]);
  const openConnect = useCallback(() => s.navigate(STUDIO_AUTOMATIONS_CONNECT), [s]);
  const selectedMedia = draft?.trigger.media_ids.map(mediaId => known[mediaId]).filter(Boolean) ?? [];
  const previewMedia = selectedMedia[0] || media.items[0] || null;
  return { status, reload: load, automation, draft, isActive, step, setStep, steps, errors, saveState, busy, gate, setGate, update, setChannel, media, loadMedia, known, selectedMedia, previewMedia, toggleMedia, activate, saveActive, goNext, openConnect, retrySave: () => persist().catch(() => setSaveState('failed')) };
}
